/**
 * SEO metadata and JSON-LD for the /today page.
 */
import type { CalendarEvent } from '../types';
import { SITE_URL, SITE_NAME } from '../brand';
import { getTemplateStr } from '../i18n';
import { formatDateLong } from '../date-format';
import { getCountries } from '../data-source';
import { getEventSlug } from '../event-slugs';
import { buildPublisherSnippet } from './json-ld';
import { YEAR, SITE_LAUNCH_DATE } from './constants';

function tpl(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? ''));
}

export function getTodaySeoData(todayStr: string, events: CalendarEvent[], lang: string) {
  const formattedDate = formatDateLong(todayStr, lang);
  const countryCodes = new Set(events.map(e => e.country));

  const closureEvents = events.filter(
    e => e.businessImpactLevel === 'critical' || e.businessImpactLevel === 'high',
  );
  const closureCount = new Set(closureEvents.map(e => e.country)).size;

  const title = getTemplateStr('seo.today.title', lang, { date: formattedDate, siteName: SITE_NAME })
    || tpl('What Holiday Is Today? — {date} | {siteName}', { date: formattedDate, siteName: SITE_NAME });

  let description: string;
  if (events.length > 0) {
    const eventNames = events.slice(0, 3).map(e => e.title).join(', ');
    description = getTemplateStr('seo.today.description', lang, {
      count: events.length, countries: countryCodes.size, date: formattedDate, events: eventNames,
    }) || tpl('{count} holidays across {countries} countries on {date}: {events}. Free daily holiday tracker.', {
      count: events.length, countries: countryCodes.size, date: formattedDate, events: eventNames,
    });
  } else {
    description = getTemplateStr('seo.today.description_empty', lang, { date: formattedDate })
      || tpl('No holidays today ({date}). See upcoming holidays this week across 14 countries.', { date: formattedDate });
  }

  const summary = events.length > 0
    ? (getTemplateStr('seo.today.summary', lang, { count: events.length, countries: countryCodes.size })
      || tpl('{count} holidays across {countries} countries today', { count: events.length, countries: countryCodes.size }))
      + (closureCount > 0
        ? (getTemplateStr('seo.today.summary_closures', lang, { closures: closureCount })
          || tpl(' · {closures} bank closures', { closures: closureCount }))
        : '')
    : '';

  return {
    title,
    description,
    canonical: `${SITE_URL}/today`,
    keywords: [
      'what holiday is today',
      'holiday today',
      'holidays today',
      'public holiday today',
      'today holiday',
      'is today a holiday',
    ],
    summary,
  };
}

export function buildTodayCollectionJsonLd(todayStr: string, events: CalendarEvent[]) {
  const countries = getCountries();
  const countryMap = new Map(countries.map(c => [c.code, c.name]));

  const eventItems = events.slice(0, 50).map((event, index) => {
    const slug = getEventSlug(event.id);
    const url = slug ? `${SITE_URL}/events/${slug}` : undefined;
    const countryName = countryMap.get(event.country) || event.country;
    return {
      '@type': 'ListItem',
      position: index + 1,
      ...(url ? { url } : {}),
      item: {
        '@type': 'Event',
        name: event.title,
        description: event.notes || `${event.title} — a ${event.type.replace(/_/g, ' ')} in ${countryName}.`,
        startDate: event.dateStart,
        endDate: event.dateEnd,
        ...(url ? { url } : {}),
        location: {
          '@type': 'Place',
          name: countryName,
          address: {
            '@type': 'PostalAddress',
            addressCountry: event.country,
          },
        },
      },
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `What Holiday Is Today? — ${todayStr}`,
    description: `Today's holidays and observances across 14 countries.`,
    url: `${SITE_URL}/today`,
    datePublished: todayStr,
    dateModified: todayStr,
    publisher: buildPublisherSnippet(),
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: events.length,
      itemListElement: eventItems,
    },
  };
}
